import { useRef, useState, useEffect, useContext, useLayoutEffect } from "react";
import { IconButton, Dialog, DialogType, Stack, Icon, mergeStyles, keyframes, Image, IStyleFunctionOrObject, IImageStyleProps, IImageStyles, MotionAnimations, MotionDurations } from "@fluentui/react";

import ReactMarkdown from "react-markdown";
import remarkGfm from 'remark-gfm';
import uuid from 'react-uuid';

import {
    ChatMessage,
    ConversationRequest,
    conversationApi,
    Citation,
    ToolMessageContent,
    ChatResponse,
    getUserInfo,
    Conversation,
    historyUpdate,
    ChatHistoryLoadingState,
    CosmosDBStatus,
    ErrorMessage,
    ChatMessageType,
    ISupportFlow,
    SupportUserInput,
    performApiRequest,
    getSupportEntry,
    SupportEntry
} from "../../../ApplicationCode/Common/Chatbot/api";
import { Answer, WelcomeMessage } from "../../Common/Chatbot/Answer";
import { QuestionInput } from "../../Common/Chatbot/QuestionInput";
import { ChatHistoryPanel } from "../../Common/Chatbot/ChatHistory/ChatHistoryPanel";
import { AppStateContext } from "../../../Core/State/ChatbotAppProvider";
import { useBoolean } from "@fluentui/react-hooks";
import { EosAppWelcomeMessage, EosSupportFlow } from "../../Common/Chatbot/Answer/EosAppSupport";
import { HttpHelper, HttpMethod } from "../../../Core/Http/HttpHelper";
import i18next, { t } from "i18next";
import { SoSmartSupportFlow } from "../../Common/Chatbot/Answer/SoSmartSupport";
import { Item } from "../../../Model/PartnerModels";
import { Loader } from "../../Common/Loader/Loader";
import { parseAnswer } from "../../Common/Chatbot/Answer/AnswerParser";
import { ChatbotContext, ChatbotHelper } from "../ChatHelper";
import { AxiosResponse } from "axios";
import { CustomerInformationContext, ICustomerInformationContext } from "../../../ApplicationCode/Customer/CustomerInformationContext";

import loadingDots from '../../../Assets/Images/loading_dots.gif';
import soLogoMini from '../../../Assets/Images/logo.png';
import { ISessionStateContext, SessionStateContext } from "../../../Core/State/SessionStateContext";
import { ChatbotMessageContext } from "../../../Core/State/ChatbotMessageContext";

const enum messageStatus {
    NotRunning = "Not Running",
    Processing = "Processing",
    Done = "Done"
}

function useInterval(callback: () => void, delay: number) {
    const savedCallback = useRef<any>();
  
    useEffect(() => {
      savedCallback.current = callback;
    }, [callback]);
  
    useEffect(() => {
      function tick() {
        savedCallback.current();
      }
  
      let id = setInterval(tick, delay);
      return () => clearInterval(id);
    }, [delay]);
  }

export interface IChatProps {
    containerStyles?: string;
    innerContainerStyles?: string;
    isEmbedded?: boolean;
};

const Chat: React.FC<IChatProps> = (props) => {
    const appStateContext = useContext(AppStateContext)
    // messageContext potrebbe non essere disponibile se Chat è usato fuori dal ChatbotMessageProvider
    const messageContext = useContext(ChatbotMessageContext);
    const chatMessageStreamEnd = useRef<HTMLDivElement | null>(null);
    const chatMessageContainer = useRef<HTMLDivElement | null>(null);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [showLoadingMessage, setShowLoadingMessage] = useState<boolean>(false);
    const [isSupportInProgress, setIsSupportInProgress] = useState<boolean>(false);
    const [supportStep, setSupportStep] = useState<number>(0);
    const [activeCitation, setActiveCitation] = useState<[content: string, id: string, title: string, filepath: string, url: string, metadata: string]>();
    const [isCitationPanelOpen, setIsCitationPanelOpen] = useState<boolean>(false);
    const abortFuncs = useRef([] as AbortController[]);
    const [showAuthMessage, setShowAuthMessage] = useState<boolean>(true);
    const [messages, setMessages] = useState<ChatMessage[]>([])
    const [processMessages, setProcessMessages] = useState<messageStatus>(messageStatus.NotRunning);
    const [clearingChat, setClearingChat] = useState<boolean>(false);
    const [hideErrorDialog, { toggle: toggleErrorDialog }] = useBoolean(true);
    const [errorMsg, setErrorMsg] = useState<ErrorMessage | null>();
    const [supportFlowHandler, setSupportFlowHandler] = useState<ISupportFlow | undefined>();
    const [showFastActions, setShowFastActions] = useState<boolean>(false);
    const [selectedApp, setSelectedApp] = useState<Item>();
    const [items, setItems] = useState<Item[]>([]);
    const [firstLoading, setFirstLoading] = useState<boolean>(false);
    const [generetingText, setGeneretingText] = useState<string>("");
    const [displayText, setDisplayText] = useState<string>("");
    const [intervalId, setIntervalId] = useState<NodeJS.Timeout | undefined>(undefined);
    const [welcomeMessage, setWelcomeMessage] = useState<string>("");

    const customerInfoContext: ICustomerInformationContext = useContext(CustomerInformationContext);
    const sessionStateContext: ISessionStateContext = useContext(SessionStateContext);

    const errorDialogContentProps = {
        type: DialogType.close,
        title: errorMsg?.title,
        closeButtonAriaLabel: 'Close',
        subText: errorMsg?.subtitle,
    };

    const modalProps = {
        titleAriaId: 'labelId',
        subtitleAriaId: 'subTextId',
        isBlocking: true,
        styles: { main: { maxWidth: 450 } },
    }

    //#region Styles

    const chatContainerStyles = mergeStyles([{
        margin: props.isEmbedded ? "25px" : "none",
        height: "100%",
        gap: "20px"
    }]);

    const chatRootStyles = mergeStyles([{
        display: 'flex',
        height: '100%',
        width: '100%'
    }]);

    const chatInnerContainerStyles = mergeStyles([{
        display: 'flex',
        flexGrow: 1,
        flexDirection: 'column',
        height: '100%',
        maxHeight: 'calc(100dvh - 110px)',
        width: props.isEmbedded ? "100%" : 'calc(100% - 15dvw)',
        maxWidth: props.isEmbedded ? "100%" : 'calc(100% - 15dvw)',
        margin: '0 auto',
        fontFamily: "Nunito",
    }]);

    const chatEmptyStateStyles = mergeStyles([{
        alignItems: "center"
    }]);

    const chatIconStyles = mergeStyles([{
        height: "151px",
        width: "151px"
    }]);

    const chatEmptyStateTitleStyles = mergeStyles([{

    }]);

    const chatEmptyStateSubtitleStyles = mergeStyles([{

    }]);

    const chatMessageStreamStyles = mergeStyles([{
        display: 'flex',
        flexGrow: 1,
        flexDirection: 'column',
        width: "100%",
        overflowY: "auto"
    }]);

    const chatMessageUserStyles = mergeStyles([{
        marginBottom: "12px",
        marginTop: "12px",
        display: "flex",
        flexDirection: "row",
        justifyContent: "flex-end",
    }]);

    const chatMessageUserInnerContainerStyles = mergeStyles([{
        display: "flex",
        flexDirection: "row",
        justifyContent: "flex-end",
        justifyItems: "flex-end",
        alignItems: "center",
        padding: "1dvw",
        fontFamily: "Nunito",
        color: "#FFFFFF",
        fontWeight: 400,
        width: "50%",
    }]);

    const chatMessageUserMessageStyles = mergeStyles([{
        display: "flex",
        flexDirection: "row",
        justifyContent: "flex-end",
        justifyItems: "flex-end",
        alignItems: "center",
        padding: ".875rem",
        fontFamily: "Nunito",
        color: "#FFFFFF",
        fontWeight: 500,
        fontSize: "clamp(11px, 0.7dvw, 18px)",
        backgroundColor: ChatbotHelper.getQuestionBackground(),
        borderRadius: '8px',
    }]);

    const chatMessageGptStyles = mergeStyles([
        {
            width: "100%",
            gap: '1dvh',
            marginBottom: "12px",
            marginTop: "12px",
            display: "flex",
            flexDirection: "row",
            justifyContent: "flex-start",
            animation: MotionAnimations.slideRightIn,
            animationDuration: MotionDurations.duration4
        }
    ]);

    const chatMessageAnswerLogoStyles = mergeStyles([{
        width: "auto",
    }]);

    const chatMessageAnswerContentStyles = mergeStyles([{
        width: "auto",
        maxWidth: "calc(100% - 3dvw)",
    }]);

    const chatMessageLogoStyle: IStyleFunctionOrObject<IImageStyleProps, IImageStyles> = {
        image: {
            width: '2dvw',
            height: 'auto'
        }
    }

    const chatMessageDotsStyle: IStyleFunctionOrObject<IImageStyleProps, IImageStyles> = {
        image: {
            width: '2dvw',
            height: 'auto'
        }
    }

    const chatMessageErrorStyles = mergeStyles([{
        padding: "20px",
        borderRadius: "8px",
        boxShadow: "rgba(182, 52, 67, 1) 1px 1px 2px, rgba(182, 52, 67, 1) 0px 0px 1px",
        color: "#242424",
        fontFamily: "Nunito",
        flex: "none",
        order: "0",
        flexGrow: "0",
        maxWidth: "800px",
        marginBottom: "10px",
        marginTop: "10px"
    }]);

    const chatMessageErrorContentStyles = mergeStyles([{
        fontFamily: "Nunito",
        fontStyle: "normal",
        fontWeight: "400",
        fontSize: "clamp(11px, 0.7dvw, 18px)",
        lineHeight: "22px",
        whiteSpace: "pre-wrap",
        wordWrap: "break-word",
        gap: "12px",
        alignItems: "center"
    }]);

    const chatInputStyles = mergeStyles([{
        position: "sticky",
        flex: "0 0 100px",
        paddingTop: "12px",
        paddingBottom: "24px",
        paddingLeft: "24px",
        paddingRight: "24px",
        marginbottom: "50px",
        margintop: "8px",
        fontFamily: "Nunito",
    }]);

    const chatInputWithFastActionsStyles = mergeStyles([{
        position: "sticky",
        flex: "0 0 100px",
        paddingTop: "12px",
        paddingBottom: "24px",
        paddingLeft: "24px",
        paddingRight: "24px",
        width: "calc(100% - 100px)",
        maxWidth: "1028px",
        marginBottom: "50px",
        marginTop: "60px",
        fontFamily: "Nunito"
    }]);

    const errorIconStyles = mergeStyles([{

    }]);

    const supportFastActionsIn = keyframes({
        "0%": {
            bottom: "0px",
            opacity: "0",
        },
        "100%": {
            bottom: "116px",
            opacity: "1"
        }
    });

    const supportFastActionsContainerStyles = mergeStyles([{
        position: "absolute",
        height: "32px",
        bottom: "116px",
        left: "0px",
        animation: supportFastActionsIn + " 1s normal"
    }]);

    const citationPanelStyles = mergeStyles([{
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-start",
        padding: "16px 16px",
        gap: "8px",
        background: "#FFFFFF",
        boxShadow: "0px 0px 16px 4px rgba(0, 0, 0, 0.14), 0px 0px 2px rgba(0, 0, 0, 0.12)",
        borderBottomLeftRadius: "8px",
        borderBottomRightRadius: "8px",
        flex: "auto",
        order: "0",
        alignSelf: "stretch",
        flexGrow: "0.3",
        maxWidth: "30%",
        overflowY: "scroll"
    }]);

    const citationPanelHeaderContainerStyles = mergeStyles([{
        width: "100%"
    }]);

    const citationPanelHeaderStyles = mergeStyles([{
        fontStyle: "normal",
        fontWeight: "600",
        fontSize: "18px",
        lineHeight: "24px",
        color: "#000000",
        flex: "none",
        order: "0",
        flexGrow: "0",
        fontFamily: "Nunito"
    }]);

    const citationPanelTitleStyles = mergeStyles([{
        fontStyle: "normal",
        fontWeight: "600",
        fontsize: "16px",
        lineheight: "22px",
        color: "#323130",
        marginTop: "12px",
        marginBottom: "12px",
        fontFamily: "Nunito"
    }]);
    
    const citationPanelContentStyles = mergeStyles([{
        fontStyle: "normal",
        fontWeight: "400",
        fontSize: "14px",
        lineHeight: "20px",
        color: "#000000",
        flex: "none",
        order: "1",
        alignSelf: "stretch",
        flexGrow: "0",
        fontFamily: "Nunito"
    }]);

    //#endregion
    
    //#region Procedure

    useEffect(() => {
        if(appStateContext?.state.isCosmosDBAvailable?.status === CosmosDBStatus.NotWorking && appStateContext.state.chatHistoryLoadingState === ChatHistoryLoadingState.Fail && hideErrorDialog){
            let subtitle = `${appStateContext.state.isCosmosDBAvailable.status}. Please contact the site administrator.`
            setErrorMsg({
                title: "Chat history is not enabled",
                subtitle: subtitle
            })
            toggleErrorDialog();
        }
    }, [appStateContext?.state.isCosmosDBAvailable]);

    // Update message count for form enablement
    useEffect(() => {
        if (messageContext && 'updateMessageCount' in messageContext && messages) {
            messageContext.updateMessageCount(messages.length);
        }
    }, [messages, messageContext]);


    useEffect(() => {
        if (generetingText.length > 0)
        {
            let i = displayText.length > 0 ? displayText.length - 1 : 0;
            setIntervalId(setInterval(() => {
                if (i < generetingText.length)
                {
                    setDisplayText(generetingText.slice(0, i));
                    i++;
                }
            }, 35));
            
            return () => clearInterval(intervalId!);
        }
      }, [generetingText]);

    const handleErrorDialogClose = () => {
        toggleErrorDialog()
        setTimeout(() => {
            setErrorMsg(null)
        }, 500);
    }
    
    const createSupportFlowHandler = () : ISupportFlow => {
        let context = ChatbotHelper.getContext();
        switch (context)
        {
            case ChatbotContext.Eos:
                return new EosSupportFlow();
            case ChatbotContext.SoSmart:
                return new SoSmartSupportFlow(customerInfoContext, sessionStateContext);
        }
        return new EosSupportFlow();
    }

    const startSupportRequestFlow = (conversation: Conversation | null | undefined) => {
        setProcessMessages(messageStatus.Processing);
        if (isSupportInProgress)
        {
            setProcessMessages(messageStatus.Done);
            return;   
        }

        setShowLoadingMessage(true);

        if (! conversation)
            conversation = appStateContext?.state?.currentChat;

        let supportId = HttpHelper.getParameter("supportId");
        let currentHandler = createSupportFlowHandler();
        currentHandler.loadRequiredData(supportId).then(() => {
            let currentSupportStep = currentHandler.initializeStartStep();
            setSupportFlowHandler(currentHandler);
            currentHandler.handleStep(selectedApp!, currentSupportStep, undefined, appStateContext!, conversation!, setMessages, makeApiRequestWithoutCosmosDB)
            .then((increment: number) => {
                setSupportStep(currentSupportStep + increment);
                setProcessMessages(messageStatus.Done);
                setIsSupportInProgress(true);
                setShowLoadingMessage(false);
            });
        });
    }
    
    const getUserInfoList = async () => {
        const userInfoList = await getUserInfo();
        if (userInfoList.length === 0 && window.location.hostname !== "localhost") {
            setShowAuthMessage(true);
        }
        else {
            setShowAuthMessage(false);
        }
    }

    const handleUserInput = async (question: SupportUserInput, conversationId?: string) => {
        if (isSupportInProgress)
        {
            setProcessMessages(messageStatus.Processing);
            let conversation: Conversation | null | undefined;
            conversation = appStateContext?.state?.currentChat;
            if (! conversation)
                return;
            
            if (supportFlowHandler?.showUserMessage(supportStep))
            {
                const userMessage: ChatMessage = {
                    id: uuid(),
                    role: "user",
                    content: question.value,
                    date: new Date().toISOString(),
                    type: ChatMessageType.Conversation
                };

                conversation.messages.push(userMessage);
                appStateContext?.dispatch({ type: 'UPDATE_CURRENT_CHAT', payload: conversation });
                setMessages(conversation.messages);
            }

            try 
            {
                let newStep = supportStep;
                let stepToAdd: number = await supportFlowHandler!.handleStep(selectedApp!, supportStep, question, appStateContext!, conversation, setMessages, makeApiRequestWithoutCosmosDB);

                newStep += stepToAdd;
                setSupportFlowHandler(supportFlowHandler);
                setSupportStep(newStep);

                let isFlowCompleted: boolean = supportFlowHandler!.isFlowCompleted(newStep);
                setIsSupportInProgress(! isFlowCompleted);
            }
            catch(err)
            {
                conversation.messages.push({
                    id: uuid(),
                    role: "error",
                    content: String(err),
                    date: new Date().toISOString(),
                    type: ChatMessageType.Conversation
                });

                let isErrorHandled = supportFlowHandler?.isErrorHandled(supportStep);
                if (! isErrorHandled)
                    setIsSupportInProgress(false);

                appStateContext?.dispatch({ type: 'UPDATE_CURRENT_CHAT', payload: conversation });
                setMessages(conversation.messages);

            }
            setProcessMessages(messageStatus.Done);
        }
        else {
            if (! conversationId)
            {
                let conversation: Conversation | null | undefined;
                conversation = appStateContext?.state?.currentChat;
                if (conversation)
                    conversationId = conversation.id;
            }
            
            makeApiRequestWithoutCosmosDB(question.value, conversationId);

        }
    }

    const getItems = async (): Promise<Item[]> => {
        const response: AxiosResponse<any, any> = await performApiRequest("/api/EosChat/item/EosApps/false/true", HttpMethod.GET);
        if (response.status == 200)
            return await response.data;

        return [];
    }

    const getWelcomeMessage = async (): Promise<string> => {
        let languageCode = i18next.resolvedLanguage.toUpperCase();

        const response: AxiosResponse<any, any> = await performApiRequest("/api/EosChat/message/" + languageCode + "/welcome", HttpMethod.GET);

        if (response.status == 200)
            return await response.data;
        
        throw new Error("Cannot read data from server API");
    }

    const makeApiRequestWithoutCosmosDB = async (question: string, conversationId?: string, skipMessageCreation?: boolean) => {
        setIsLoading(true);
        setShowLoadingMessage(true);
        setGeneretingText("");
        setDisplayText("");
        
        const abortController = new AbortController();
        abortFuncs.current.unshift(abortController);


        let userMessage: ChatMessage = {
            id: uuid(),
            role: "user",
            content: question,
            date: new Date().toISOString(),
            type: ChatMessageType.Conversation
        };

        let conversation: Conversation | null | undefined;
        if(!conversationId){
            conversation = {
                id: conversationId ?? uuid(),
                title: question,
                messages: [userMessage],
                date: new Date().toISOString(),
                languageCode: i18next.resolvedLanguage.toUpperCase()
            }
        }else{
            conversation = appStateContext?.state?.currentChat
            if(!conversation){
                console.error("Conversation not found.");
                setIsLoading(false);
                setShowLoadingMessage(false);
                abortFuncs.current = abortFuncs.current.filter(a => a !== abortController);
                return;
            }
            else if (! skipMessageCreation)
                conversation.messages.push(userMessage);
        }

        appStateContext?.dispatch({ type: 'UPDATE_CURRENT_CHAT', payload: conversation });
        setMessages(conversation.messages);

        //handle Support Request
        const userRequiredSupport: boolean = question.toLowerCase() === "support";
        if(userRequiredSupport){
            setIsLoading(false);
            setShowLoadingMessage(false);
            setProcessMessages(messageStatus.Done);

            startSupportRequestFlow(conversation);

            return abortController.abort();
        }
        
        const request: ConversationRequest = {
            messages: [...conversation.messages.filter((answer) => answer.role !== "error")],
            languageCode: i18next.resolvedLanguage.toUpperCase()
            // messages: [...conversation.messages.filter((answer) => answer.role === "error")]
        };

        let supportRequired: boolean = false;
        let result = {} as ChatResponse;
        try {
            let response = await conversationApi(request, abortController.signal);
            if (response?.body) {
                let reader = response.body.getReader();
                let runningText = "";
                let currentText = "";

                while (true) {
                    setProcessMessages(messageStatus.Processing);
                    const {done, value} = await reader.read();
                    if (done) break;

                    var text = new TextDecoder("utf-8").decode(value);
                    const objects = text.split("\n");
                    objects.forEach((obj) => {
                        try {
                            runningText += obj;
                            result = JSON.parse(runningText);
                            result.choices[0].messages.forEach((obj) => {
                                obj.id = uuid();
                                obj.date = new Date().toISOString();
                                obj.type = ChatMessageType.Conversation
                            })
                            

                            for (let i = 0; i < result.choices[0].messages.length; i++)
                            {
                                let startPos = currentText.length > 0 ? currentText.length : 0;
                                currentText += result.choices[0].messages[0].content.slice(startPos, result.choices[0].messages[i].content.length - 1);
                            }
                        
                            runningText = "";
                        }
                        catch { }
                    });

                    setGeneretingText(currentText);
                }
                
                await reader.cancel();
                reader.releaseLock();
            
                let parsedAnswer = parseAnswer({
                    answer: result.choices[0].messages[0].content.toString(),
                    citations: []
                })

                if (parsedAnswer.citations.length > 0)
                {
                    const questionRequest: ConversationRequest = {
                        messages: [...messages.filter((answer) => answer.role !== "error"), ...result.choices[0].messages],
                        languageCode: i18next.resolvedLanguage.toUpperCase()
                        // messages: [...conversation.messages.filter((answer) => answer.role === "error")]
                    };
                    result.choices[0].messages[0].followupQuestions = [];
                }
                conversation.messages.push(...result.choices[0].messages);
                appStateContext?.dispatch({ type: 'UPDATE_CURRENT_CHAT', payload: conversation });
                setDisplayText("");
                setGeneretingText("");
                setMessages([...messages, ...result.choices[0].messages]);
            }
        } catch ( e )  {
            if (!abortController.signal.aborted) {
                let errorMessage = t("chatbot:chat:errorMessage");
                if (result.error?.message) {
                    errorMessage = result.error.message;
                }
                else if (typeof result.error === "string") {
                    errorMessage = result.error;
                }
                else { 
                    //The async reader may not have finished reading the response so we need to check if the result message is an error
                    let chatCompletionError = result as any;
                    if(chatCompletionError.message){
                        errorMessage = chatCompletionError.message;
                    }
                }

                let errorChatMsg: ChatMessage = {
                    id: uuid(),
                    role: "error",
                    content: errorMessage,
                    date: new Date().toISOString(),
                    type: ChatMessageType.Conversation
                }
                conversation.messages.push(errorChatMsg);
                appStateContext?.dispatch({ type: 'UPDATE_CURRENT_CHAT', payload: conversation });
                setMessages([...messages, errorChatMsg]);
            } else {
                setMessages([...messages, userMessage])
            }
        } finally {
            setIsLoading(false);
            setShowLoadingMessage(false);
            abortFuncs.current = abortFuncs.current.filter(a => a !== abortController);
            setProcessMessages(messageStatus.Done);
            setGeneretingText("");
            setDisplayText("");
        }

        return abortController.abort();
    };

    const newChat = () => {
        setProcessMessages(messageStatus.Processing)
        appStateContext?.dispatch({ type: 'CLEAR_CURRENT_CHAT' });
        setWelcomeMessage("");
        setDisplayText("");
        setGeneretingText("");
        try 
        {
            clearInterval(intervalId!);
        }
        catch
        {}
        setMessages([]);
        setIsSupportInProgress(false);
        setSupportStep(0);
        setSelectedApp(new Item());
        setIsCitationPanelOpen(false);
        setActiveCitation(undefined);
        setProcessMessages(messageStatus.Done);
        setFirstLoading(false);
        window.history.replaceState(null, "", HttpHelper.removeParameter("supportId"));

        loadInitialData().finally(() => {
        });
    };

    const stopGenerating = () => {
        abortFuncs.current.forEach(a => a.abort());
        setDisplayText("");
        setGeneretingText("");
        try 
        {
            clearInterval(intervalId!);
        }
        catch
        {}

        let text = generetingText;
        if (text)
        {
            const conversation = appStateContext?.state?.currentChat
            let chatMsg: ChatMessage = {
                id: uuid(),
                role: "assistant",
                content: text,
                date: new Date().toISOString(),
                type: ChatMessageType.Conversation
            }
            conversation!.messages.push(chatMsg);
            appStateContext?.dispatch({ type: 'UPDATE_CURRENT_CHAT', payload: conversation! });
            setMessages([...messages, chatMsg]);
        }
        setShowLoadingMessage(false);
        setIsLoading(false);
    }

    const enableDisableClearButton = () => {
        const clearButton = document.getElementById('clearCurrentChat');
        const clearButtonIcon = document.getElementById('ICON-clearCurrentChat');

        if(clearButton !== null){
            if(isClearButtonDisabled()){
                clearButton.setAttribute("disabled","");

                clearButton.style.background = "#BDBDBD";
                clearButton.style.cursor = "";
            }
            else{
                clearButton.removeAttribute("disabled");
                
                clearButton.style.removeProperty("background");
                clearButton.style.removeProperty("cursor");
                clearButton.onclick = () => newChat();
            }

            if(clearButtonIcon !== null){
                if(isClearButtonDisabled()){
                    clearButtonIcon.style.color = "#FFFF";
                    clearButtonIcon.style.cursor = "";
                }
                else{
                    clearButtonIcon.style.removeProperty("color");
                    clearButtonIcon.style.removeProperty("cursor");
                }
            }
        }
    }

    const enableDisableSupportButton = () => {
        const supportButton = document.getElementById('btnSupportRequest');
        const supportButtonIcon = document.getElementById('ICON-btnSupportRequest');

        if(supportButton !== null){
            if(isSupportButtonDisabled()){
                supportButton.setAttribute("disabled","");

                supportButton.style.background = "#BDBDBD";
                supportButton.style.cursor = "";
            }
            else{
                supportButton.removeAttribute("disabled");
                
                supportButton.style.removeProperty("background");
                supportButton.style.removeProperty("cursor");
            }

            if(supportButtonIcon !== null){
                if(isSupportButtonDisabled()){
                    supportButtonIcon.style.color = "#FFFF";
                    supportButtonIcon.style.cursor = "";
                }
                else{
                    supportButtonIcon.style.removeProperty("color");
                    supportButtonIcon.style.removeProperty("cursor");
                }
            }
        }
    }

    const createSoSmartSupportWelcomeMessage = (supportEntry: SupportEntry): string => {
        const returnChar = "  \n";
        const returnCharDouble = "  \n\n";
        const greating = supportEntry.userFullName ? t('chatbot:supportGreetingSpecific') + supportEntry.userFullName + "," : t('chatbot:supportGreetingGeneral') + ",";
        
        const details = [
            { key: 'chatbot:supportLicense', value: supportEntry.license },
            { key: 'chatbot:supportCompanyName', value: supportEntry.companyName },
            { key: 'chatbot:supportEnvironmentName', value: supportEntry.environmentName },
            { key: 'chatbot:supportRole', value: supportEntry.role },
            { key: 'chatbot:supportPageName', value: supportEntry.pageName }
        ].filter(detail => detail.value).map(detail => t(detail.key) + detail.value + "*" + returnChar).join('');
    
        const lastErrorDetail = supportEntry.lastError ? returnCharDouble + t('chatbot:supportLastError') + supportEntry.lastError + "*" : '';

        const supportHelp = `${t('chatbot:supportHelp1')}${returnChar}${t('chatbot:supportHelp2')}${returnChar}${t('chatbot:supportHelp3')}`;

        return `${greating}${returnChar}${t('chatbot:supportInfo')}${returnCharDouble}${details}${lastErrorDetail}${returnCharDouble}${supportHelp}${returnCharDouble}${t('chatbot:supportQuestion')}${returnCharDouble}`;
    };

    const createConversation = (id: string, title: string, messages: ChatMessage[] = []): Conversation => ({
        id,
        title,
        languageCode: i18next.resolvedLanguage.toUpperCase(),
        messages,
        date: new Date().toISOString(),
    });
    
    const updateConversation = (conversation: Conversation) => {
        setMessages(conversation.messages);
        appStateContext?.dispatch({ type: 'UPDATE_CURRENT_CHAT', payload: conversation });
        setProcessMessages(messageStatus.Done);
    };

    const sendWelcomeMessage = (): Promise<Conversation> => {
        return new Promise((resolve, reject) => {
            setProcessMessages(messageStatus.Processing);
    
            const currentSupportId = HttpHelper.getParameter("supportId"); 
            if (currentSupportId) {
                getSupportEntry(currentSupportId).then(entry => {
                    const questionMessage: ChatMessage = {
                        id: uuid(),
                        role: "assistant",
                        content: createSoSmartSupportWelcomeMessage(entry),
                        date: new Date().toISOString(),
                        type: ChatMessageType.Conversation
                    };

                    const conversation = appStateContext?.state?.currentChat || createConversation(uuid(), "Business Central support request", [questionMessage]);
                    resolve(conversation);
                }).catch(err => {
                    console.log(err);
                    reject(false);
                });
            } 
            else {
                let questionMessage: ChatMessage;
                let context = ChatbotHelper.getContext();
                if (context == ChatbotContext.Eos)
                {
                    questionMessage = {
                        id: uuid(),
                        role: "assistant",
                        content: t("chatbot:chat:welcomeMessage")!,
                        date: new Date().toISOString(),
                        type: ChatMessageType.BotRequest,
                        subType: "eosWelcomeMessage"
                    };
                }
                else
                {
                    questionMessage = {
                        id: uuid(),
                        role: "assistant",
                        content: welcomeMessage,
                        isLogged: true,
                        date: new Date().toISOString(),
                        type: ChatMessageType.Conversation,
                        subType: ""
                    };
                }

                const conversation = appStateContext?.state?.currentChat || createConversation(uuid(), welcomeMessage, [questionMessage]);
                resolve(conversation);
            }
        });
    };

    const sendOpenSupportMessage = () => {
        let supportUserInput: SupportUserInput = {
            key: 'support',
            value: 'support'
        };

        handleUserInput(supportUserInput);
    }

    useEffect(() => {
        enableDisableClearButton();
        enableDisableSupportButton();
        
        if (appStateContext?.state.currentChat) {
            setMessages(appStateContext.state.currentChat.messages);
            
            //dispatchSupportEvent();
        }else{
            setMessages([])
        }

    }, [appStateContext?.state.currentChat]);
    
    useLayoutEffect(() => {
        const saveToDB = async (messages: ChatMessage[], id: string) => {
            const response = await historyUpdate(messages, id)
            return response
        }

        if (appStateContext && appStateContext.state.currentChat && processMessages === messageStatus.Done) {
                if(appStateContext.state.isCosmosDBAvailable.cosmosDB){
                    if(!appStateContext?.state.currentChat?.messages){
                        console.error("Failure fetching current chat state.")
                        return 
                    }
                    saveToDB(appStateContext.state.currentChat.messages, appStateContext.state.currentChat.id)
                    .then((res) => {
                        if(!res.ok){
                            let errorMessage = "An error occurred. Answers can't be saved at this time. If the problem persists, please contact the site administrator.";
                            let errorChatMsg: ChatMessage = {
                                id: uuid(),
                                role: "error",
                                content: errorMessage,
                                date: new Date().toISOString(),
                                type: ChatMessageType.Conversation
                            }
                            if(!appStateContext?.state.currentChat?.messages){
                                let err: Error = {
                                    ...new Error,
                                    message: "Failure fetching current chat state."
                                }
                                throw err
                            }
                            setMessages([...appStateContext?.state.currentChat?.messages, errorChatMsg])
                        }
                        return res as Response
                    })
                    .catch((err) => {
                        console.error("Error: ", err)
                        let errRes: Response = {
                            ...new Response,
                            ok: false,
                            status: 500,
                        }
                        return errRes;
                    })
                }else{
                }
                appStateContext?.dispatch({ type: 'UPDATE_CHAT_HISTORY', payload: appStateContext.state.currentChat });
                setMessages(appStateContext.state.currentChat.messages);
            setProcessMessages(messageStatus.NotRunning)
        }
    }, [processMessages]);

    useEffect(() => {
        getUserInfoList();

        ChatbotHelper.handleContext();
        loadInitialData();
    }, []);

    useEffect(() => {
        if (welcomeMessage)
        {
            sendWelcomeMessage().then((resp) => {
                updateConversation(resp);
                setFirstLoading(true);
            })
            .catch(() => {
                newChat();
                setFirstLoading(true);
            });
        }
    }, [welcomeMessage])

    const loadInitialData = async () : Promise<boolean> => {
        try 
        {
            let message = await getWelcomeMessage();
            if (ChatbotHelper.getContext() == ChatbotContext.Eos)
            {
                let itemsData = await getItems();
                setItems(itemsData);
            }
            else
                setItems([]);
            setWelcomeMessage(message);
            return true;
        }
        catch
        {
            return false;
        }
    }

    useEffect(() => {
        chatMessageContainer.current?.scrollTo({
            top: chatMessageStreamEnd.current?.offsetTop!,
            left: 0,
            behavior: "smooth",
        }); 
        
        enableDisableClearButton();
        enableDisableSupportButton();
    }, [showLoadingMessage, processMessages]);

    const onShowCitation = (citation: Citation) => {
        setActiveCitation([citation.content, citation.id, citation.title ?? "", citation.filepath ?? "", "", ""]);
        setIsCitationPanelOpen(true);
    };

    const parseCitationFromMessage = (message: ChatMessage) => {
        if (message?.role && message?.role === "tool") {
            try {
                const toolMessage = JSON.parse(message.content.toString()) as ToolMessageContent;
                return toolMessage.citations;
            }
            catch {
                return [];
            }
        }
        return [];
    }

    const isClearButtonDisabled = () => {
        return isLoading || (messages && messages.length === 0) || clearingChat || appStateContext?.state.chatHistoryLoadingState === ChatHistoryLoadingState.Loading
    }

    const isSupportButtonDisabled = () => {
        return isLoading || clearingChat || isSupportInProgress
    }

    const handleSupportInput = (key: any, value: any) => {
        let userInput: SupportUserInput = {
            key: key,
            value: value,
            systemGenerated: true
        };
        
        handleUserInput(userInput);
    }

    const handleElement = (msg: ChatMessage) : JSX.Element => {
        switch (msg.subType)
        {
            case "welcomeMessage":
                return <WelcomeMessage isSupportInProgress={isSupportInProgress} onLinkClick={sendOpenSupportMessage} />
            case "eosWelcomeMessage":
                return <EosAppWelcomeMessage items={items} OnSelectApp={(app) => {
                    setSelectedApp(app);

                    const appMessage: ChatMessage = {
                        id: uuid(),
                        role: "assistant",
                        content: t("chatbot:eosAppWelcome:selection")!.replace("#APPDESCRIPTION#", app.description),
                        date: new Date().toISOString(),
                        type: ChatMessageType.Conversation
                    };
    
                    let conversation = appStateContext?.state?.currentChat;
                    conversation?.messages.push(appMessage);
                    appStateContext?.dispatch({ type: 'UPDATE_CURRENT_CHAT', payload: conversation! });
                    setMessages(conversation!.messages);
                }} />
            default:
                return supportFlowHandler!.handleElement(msg, handleSupportInput);
        }
        return <></>;
    }

    const onQuestionInput = (question: string, conversationId?: string) => {
        if ((selectedApp?.itemErpNo) && (!isSupportInProgress))
        {
            let aus = question;
            question = t("chatbot:eosAppWelcome:prefix")!.replace("#APP#", selectedApp!.description).replace("#QUESTION#", aus);
        }

        let questionUserInput: SupportUserInput = {
            key: 'question',
            value: question
        };

        handleUserInput(questionUserInput, conversationId);
    }

    const handleFastActionsElements = (): JSX.Element[] => {
        let fastActions: JSX.Element[] = [];       
        if ((isSupportInProgress) && (supportFlowHandler) && (!isLoading))
        {
            let supportActions = supportFlowHandler.handleFastActions(supportStep, handleSupportInput);
            if (supportActions)
                fastActions.push(supportActions);
        }

        
        let show = fastActions.length > 0;
        if (show != showFastActions)    
            setShowFastActions(show);

        return fastActions;
    }
    
    let chatInputStyle = chatInputStyles;
    if ((showFastActions) && (HttpHelper.getParameter("embedded") != "true"))
        chatInputStyle = chatInputWithFastActionsStyles;

    //#endregion

    if (firstLoading)
    {
        return (
            <Stack className={ props.containerStyles ? props.containerStyles : chatContainerStyles} role="main">
                <Stack horizontal className={chatRootStyles}>
                    <div className={ props.innerContainerStyles ? props.innerContainerStyles : chatInnerContainerStyles}>
                        {
                            !messages || messages.length < 1 ? 
                            (
                                <Stack className={chatEmptyStateStyles}>
                                    <img
                                        src={ChatbotHelper.getLogo()}
                                        className={chatIconStyles}
                                        aria-hidden="true"
                                        alt="Logo"
                                    />
                                    <h1 className={chatEmptyStateTitleStyles}>{t("chatbot:chat:title")}</h1>
                                    <h2 className={chatEmptyStateSubtitleStyles}>{t("chatbot:chat:subTitle")}</h2>
                                </Stack>
                            ) 
                        : 
                            (
                                <div className={chatMessageStreamStyles} ref={chatMessageContainer} role="log">
                                    {
                                        messages.map(
                                            (answer, index) => {
                                                return (
                                                    <>
                                                        {
                                                            answer.type === ChatMessageType.Conversation ?
                                                                answer.role === "user" ?
                                                                    <div className={chatMessageUserStyles} tabIndex={0}>
                                                                        <div className={chatMessageUserInnerContainerStyles}>
                                                                            <div className={chatMessageUserMessageStyles}>
                                                                                {answer.content}
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                : 
                                                                answer.role === "assistant" ? 
                                                                    <div className={chatMessageGptStyles}>
                                                                        <Stack
                                                                            className={chatMessageAnswerLogoStyles}
                                                                        >
                                                                            <Image 
                                                                                src={soLogoMini}
                                                                                styles={chatMessageLogoStyle}
                                                                            />
                                                                        </Stack>
                                                                        <Stack
                                                                            className={chatMessageAnswerContentStyles}
                                                                        >
                                                                            <Answer
                                                                                answer={{
                                                                                    answer: answer.content.toString(),
                                                                                    citations: parseCitationFromMessage(messages[index - 1]),
                                                                                }}
                                                                                followupQuestions={answer.followupQuestions}
                                                                                onCitationClicked={c => onShowCitation(c)}
                                                                                onFollowUpQuestionClicked={(question) => {
                                                                                    setGeneretingText("");
                                                                                    setDisplayText("");
                                                                                    onQuestionInput(question, appStateContext?.state.currentChat?.id);
                                                                                }}
                                                                                onOpenSupportRequestClick={() => {
                                                                                    setGeneretingText("");
                                                                                    setDisplayText("");
                                                                                    startSupportRequestFlow(undefined);
                                                                                }}
                                                                                isLastIteraction={(index + 1) == messages.length}
                                                                                showFeedbackIcons
                                                                                feedbackMessage={answer}
                                                                            />
                                                                        </Stack>
                                                                    </div> 
                                                                : 
                                                                answer.role === "error" ? 
                                                                    <div className={chatMessageErrorStyles}>
                                                                        <Stack 
                                                                            horizontal 
                                                                            className={chatMessageErrorContentStyles}
                                                                        >
                                                                            <Icon 
                                                                                iconName="Error" 
                                                                                className={errorIconStyles} 
                                                                                style={{color: "rgba(182, 52, 67, 1)"}} 
                                                                            />
                                                                            <span>
                                                                                {t("chatbot:chat:error")}
                                                                            </span>
                                                                        </Stack>
                                                                        <span 
                                                                            className={chatMessageErrorContentStyles}
                                                                        >
                                                                            {answer.content}
                                                                        </span>
                                                                    </div> 
                                                                : 
                                                                <></>
                                                            :
                                                            answer.subType ?
                                                                <div className={chatMessageGptStyles}>
                                                                    {handleElement(answer)}
                                                                </div> 
                                                            : 
                                                            <></>
                                                        }

                                                    </>
                                                )
                                            }
                                        )
                                    }
                                    {
                                        showLoadingMessage &&
                                            <Stack
                                                horizontal
                                                horizontalAlign="start" 
                                                className={chatMessageGptStyles}
                                            >
                                                <Stack
                                                    className={chatMessageAnswerLogoStyles}
                                                >
                                                    <Image 
                                                        src={soLogoMini}
                                                        styles={chatMessageLogoStyle}
                                                    />
                                                </Stack>
                                                <Stack
                                                    className={chatMessageAnswerContentStyles}
                                                >
                                                    <Image 
                                                        src={loadingDots} 
                                                        styles={chatMessageDotsStyle}
                                                    />
                                                </Stack>
                                            </Stack>
                                    }
                                    <div ref={chatMessageStreamEnd} />
                                </div>
                            )
                        }
                        <Stack 
                            horizontal 
                            className={chatInputStyle}
                        >
                            {
                                (
                                    HttpHelper.getParameter("embedded") === "true") ?
                                        <></>
                                    :
                                    <Stack 
                                        horizontal 
                                        className={supportFastActionsContainerStyles} 
                                        tokens={{
                                            childrenGap: 15
                                        }}
                                    >
                                        {handleFastActionsElements()}
                                    </Stack>
                            }
                            <Stack>
                                <Dialog
                                    hidden={hideErrorDialog}
                                    onDismiss={handleErrorDialogClose}
                                    dialogContentProps={errorDialogContentProps}
                                    modalProps={modalProps}
                                >
                                </Dialog>
                            </Stack>
                            <QuestionInput
                                clearOnSend
                                placeholder={t("chatbot:chat:questionInput")!}
                                disabled={isLoading}
                                showSupportIcon={!((HttpHelper.getParameter("embedded") === "true") && (ChatbotHelper.getContext() != ChatbotContext.Eos) && ChatbotHelper.isPortalEnabled() || props.isEmbedded)}
                                messageCount={messages.length}
                                onSend={(question, id) => {
                                    onQuestionInput(question, id);
                                }}
                                onClearChat={() => {newChat()}}
                                onSupportRequest={() => {sendOpenSupportMessage()}}
                                onStopGenerating={stopGenerating}
                                conversationId={appStateContext?.state.currentChat?.id ? appStateContext?.state.currentChat?.id : undefined}
                                isSupportInProgress={isSupportInProgress}
                            />
                        </Stack>
                    </div>
                    {
                        messages && messages.length > 0 && isCitationPanelOpen && activeCitation &&
                            <Stack.Item 
                                className={citationPanelStyles} 
                                tabIndex={0} 
                                role="tabpanel" 
                                aria-label="Citations Panel"
                            >
                                <Stack 
                                    aria-label="Citations Panel Header Container" 
                                    horizontal 
                                    className={citationPanelHeaderContainerStyles} 
                                    horizontalAlign="space-between" 
                                    verticalAlign="center"
                                >
                                    <span 
                                        aria-label="Citations" 
                                        className={citationPanelHeaderStyles}
                                    >
                                        {t("chatbot:chat:citationDialog")!}
                                    </span>
                                    <IconButton 
                                        iconProps={{ iconName: 'Cancel'}} 
                                        aria-label="Close citations panel" 
                                        onClick={() => setIsCitationPanelOpen(false)}
                                    />
                                </Stack>
                                <h5 
                                    className={citationPanelTitleStyles} 
                                    tabIndex={0}
                                >
                                    {activeCitation[2]}
                                </h5>
                                <div tabIndex={0}> 
                                    <ReactMarkdown 
                                        linkTarget="_blank"
                                        className={citationPanelContentStyles}
                                        children={activeCitation[0]} 
                                        remarkPlugins={[remarkGfm]}
                                    />
                                </div>
                            </Stack.Item>
                    }
                    {
                        appStateContext?.state.isChatHistoryOpen && appStateContext?.state.isCosmosDBAvailable?.status !== CosmosDBStatus.NotConfigured && 
                            <ChatHistoryPanel/>
                    }
                </Stack>
            </Stack>
        );
    }
    else
        return (
            <Loader text="" />
        )
};

export default Chat;
